import { useState } from "react";
import BrandForm from "./BrandForm";
import { Tag, Space, Button, Row, Col, notification, Typography, Checkbox } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, EyeOutlined, FileExcelFilled } from "@ant-design/icons";
import { useGetBrandsQuery } from "../../redux/services/brandApi";
import TopScrollTable from "../../components/TopScrollTable";
import { Link } from "react-router-dom";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import BrandFilters from "./BrandFilters";
import { BASE_URL } from "../../redux/services/baseApi";
import { useAppSelector } from "../../redux/hooks";

const { Text } = Typography;
interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

const Brand = () => {
  const [api, contextHolder] = notification.useNotification();
  const [isOpen, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [brandModelPricingLoading, setBrandModelPricingLoading] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<any>({});
  const [queryParams, setQueryParams] = useState<any>({ include_all: true, page: 1, page_size: 20 });

  const { data, refetch, isFetching } = useGetBrandsQuery({
    ...queryParams
  });

  const auth = useAppSelector<any>((store) => store.auth);
  const brands = data?.brands || [];

  const onUpdate = (location: any) => {
    setSelectedBrand(location);
    setIsEditMode(!isEditMode);
    setOpen(!isOpen);
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code) => (
        <Text style={{ whiteSpace: "nowrap" }} key={code}>
          {code ? code : "-"}
          <br />
        </Text>
      )
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (tag) => (
        <Tag color={tag === "active" ? "green" : "red"} key={tag}>
          {tag.toUpperCase()}
        </Tag>
      )
    },

    {
      title: "Action",
      key: "action",
      render: (_, brand: any) => (
        <Space size="middle">
          <Link to={`${brand?.id}`}>
            <Button icon={<EyeOutlined />} title="View"></Button>
          </Link>
          <Button icon={<EditOutlined />} onClick={() => onUpdate(brand)} title="Update"></Button>
        </Space>
      )
    }
  ];

  const pagination = {
    pageSize: queryParams?.page_size,
    current: queryParams?.page,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onShowSizeChange: (current: any, page_size: number) => {
      const values: any = { page_size: page_size };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    },
    onChange: (page: number) => {
      const values: any = { page: page };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
  };

  const refetchWithReset = () => {
    const values: any = { page: 1 };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    refetch();
  };

  const onCancel = () => {
    setSelectedBrand(null);
    setOpen(false);
  };

  const onChangeActiveOnly = (e: CheckboxChangeEvent) => {
    const include_all: boolean = e.target.checked;
    const values: any = { include_all: !include_all };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
  };


  const exportBrandModelPricing = async () => {
    try {
      setBrandModelPricingLoading(true);

      const response = await fetch(
        `${BASE_URL}brand_models/export_pricing`,
        {
          method: "GET",
          headers: {
            "access-token": auth.accessToken,
            "token-type": "Bearer",
            client: auth.client,
            uid: auth.uid,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to export brand model pricing");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const anchorElement = document.createElement("a");
      anchorElement.href = downloadUrl;
      anchorElement.download = "brand_model_pricing.xlsx";

      document.body.appendChild(anchorElement);
      anchorElement.click();
      anchorElement.remove();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error exporting brand model pricing:", error);
    } finally {
      setBrandModelPricingLoading(false);
    }
  };


  return (
    <>
      {contextHolder}
      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <div>
            <h2>Brands</h2>
            <Text>Total brands: {data?.meta?.total_count || 0}</Text>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 25, flexWrap: "wrap" }}>
            <Button
              type="primary"
              size="large"
              loading={brandModelPricingLoading}
              icon={<FileExcelFilled />}
              onClick={exportBrandModelPricing}
            >
              {brandModelPricingLoading ? "Exporting, please wait..." : "Export Pricing"}
            </Button>

            <Link to="import-pricings">
              <Button type="primary" size="large" icon={<FileExcelFilled />}>
                Import Pricing
              </Button>
            </Link>

            <Button
              onClick={() => setOpen(!isOpen)}
              size="large"
              type="primary"
            >
              Create Brand
            </Button>
          </div>
        </Col>
      </Row>

      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <Checkbox onChange={onChangeActiveOnly}>Show Active Only</Checkbox>
        </Col>
      </Row>

      <BrandFilters
        applyFilters={(values: any) =>
          setQueryParams((oldValues: any) => ({ ...oldValues, ...values }))
        }
      />

      <TopScrollTable
        rowKey="id"
        columns={columns}
        dataSource={brands}
        bordered
        loading={isFetching}
        scroll={{ x: 400 }}
        pagination={pagination}
      />

      {isOpen && (
        <BrandForm
          onCancel={onCancel}
          isOpen={isOpen}
          brandId={selectedBrand?.id}
          refetch={refetchWithReset}
        />
      )}
    </>
  );
};

export default Brand;
